import React from 'react';
import { computeReorderEdgeOps, computeGroupReorderEdgeOps } from '@chatsdet/ui';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import {
  buildNodeQuickCreateHandlers,
  type WorkflowNodeType,
  type QuickCreateTarget,
  type BranchType,
} from '../helpers';
import { getDefaultNodeData, isTempNodeId, sanitizeNodeDataForPersistence } from '../utils';
import type { CreateNodeFn, CreateNodeOptions } from '../types';
import type { NodeCrudMutationProps } from '../mutationContracts';
import { computeOptimisticBulkDeleteGraph } from './bulkDeleteOptimistic';
import { getNodeCrudFailureToast, getNodeUpdateFailureToast } from './node-update-error-toast';
import type { UndoHistoryHandle } from './useUndoHistory';

/** A node create in flight, keyed by its client temp id. */
type PendingCreate = {
  /** Resolves to the real node id, or null if the create failed. */
  settled: Promise<string | null>;
  /** undefined while in flight; the real id once created; null if it failed. */
  realId: string | null | undefined;
  /** Latest full config for the node; every save for this node goes through here. */
  config: Record<string, any>;
  /** The config most recently handed to the server, so queued saves coalesce. */
  sent: Record<string, any> | null;
  /** Saves queued or running; at 0 after the create settles the live node is the source of truth again. */
  active: number;
  /** Serializes this node's saves so an older config can never land last. */
  chain: Promise<void>;
};

type UseNodeCrudParams = NodeCrudMutationProps & {
  workflowId: string | null;
  /** Owning flow's type — seed/teardown get a shorter default wait. */
  workflowFlowType?: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  getNodes: () => WorkflowNode[];
  setNodes: (nodes: WorkflowNode[] | ((prev: WorkflowNode[]) => WorkflowNode[])) => void;
  setEdges: (edges: WorkflowEdge[] | ((prev: WorkflowEdge[]) => WorkflowEdge[])) => void;
  selectedNodeForConfig: WorkflowNode | null;
  setSelectedNodeForConfig: React.Dispatch<React.SetStateAction<WorkflowNode | null>>;
  setSelectedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleNodeConfig: (node: WorkflowNode) => void;
  handleQuickCreateNode: (sourceNodeId: string, nodeType: QuickCreateTarget) => void;
  handleBranchQuickCreate: (
    sourceNodeId: string,
    nodeType: QuickCreateTarget,
    branchType: BranchType
  ) => void;
  hasApiCatalog: boolean;
  hasSandboxActions: boolean;
  hasMcp: boolean;
  ensureWorkflow: () => Promise<string>;
  workflowDetailRefetch: () => void;
  toast: {
    error: (msg: string, options?: { reportError?: boolean }) => void;
    info: (msg: string) => void;
  };
  undoHistory: UndoHistoryHandle;
  setCachePopover: (popover: { nodeId: string; position: { x: number; y: number } } | null) => void;
  selectedEdgeId: string | null;
  clearEdgeSelection: () => void;
  setIsInsertingNode: (v: boolean) => void;
  autoSaveIfDirty: () => Promise<void>;
  selectSidebarTab: (tab: any) => void;
  onEdit?: () => void;
  /**
   * Suggest-changes mode flag. While true, edits stage locally (the mutations
   * are shimmed upstream). Multi-node delete normally relies on a server refetch
   * to reconnect the chain; in suggest mode there's no refetch, so we compute the
   * reconnect edges locally with the same algorithm the server uses.
   */
  suggestingRef?: React.MutableRefObject<boolean>;
};

export function useNodeCrud({
  workflowId,
  workflowFlowType,
  nodes,
  edges,
  getNodes,
  setNodes,
  setEdges,
  selectedNodeForConfig,
  setSelectedNodeForConfig,
  setSelectedNodeIds,
  handleNodeConfig,
  handleQuickCreateNode,
  handleBranchQuickCreate,
  hasApiCatalog,
  hasSandboxActions,
  hasMcp,
  ensureWorkflow,
  createNodeMutation,
  updateNodeMutation,
  deleteNodeMutation,
  bulkDeleteNodesMutation,
  createEdgeMutation,
  deleteEdgeMutation,
  insertNodeOnEdgeMutation,
  workflowDetailRefetch,
  toast,
  undoHistory,
  setCachePopover: _setCachePopover,
  selectedEdgeId,
  clearEdgeSelection,
  setIsInsertingNode,
  autoSaveIfDirty,
  selectSidebarTab,
  onEdit,
  suggestingRef,
}: UseNodeCrudParams) {
  const onCreateNodeRef = React.useRef<CreateNodeFn | null>(null);

  // `workflowFlowType` arrives asynchronously from the workflow query, while the
  // create callbacks below are memoized on dependency sets that predate it.
  // Reading it through a ref keeps new wait nodes on the correct seed/teardown
  // default instead of whatever the flow type was at first render.
  const workflowFlowTypeRef = React.useRef(workflowFlowType);
  React.useEffect(() => {
    workflowFlowTypeRef.current = workflowFlowType;
  }, [workflowFlowType]);

  // Handle node deletion
  const handleVerticalCanvasNodeDelete = React.useCallback(
    async (nodeId: string) => {
      onEdit?.();
      undoHistory.captureSnapshot('Delete node');
      const incomingEdges = edges.filter((e) => e.target === nodeId);
      const outgoingEdges = edges.filter((e) => e.source === nodeId);
      const shouldAutoConnect = incomingEdges.length === 1 && outgoingEdges.length === 1;
      const parentNodeId = incomingEdges[0]?.source;
      const childNodeId = outgoingEdges[0]?.target;
      const tempEdgeId = shouldAutoConnect ? `temp-edge-${Date.now()}` : null;

      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => {
        const filtered = eds.filter((e) => e.source !== nodeId && e.target !== nodeId);
        if (shouldAutoConnect && parentNodeId && childNodeId && tempEdgeId) {
          return [...filtered, { id: tempEdgeId, source: parentNodeId, target: childNodeId }];
        }
        return filtered;
      });

      if (selectedNodeForConfig?.id === nodeId) {
        setSelectedNodeForConfig(null);
      }
      setSelectedNodeIds((prev) => {
        if (!prev.has(nodeId)) return prev;
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });

      try {
        await deleteNodeMutation.mutateAsync(nodeId);
        if (shouldAutoConnect && parentNodeId && childNodeId && workflowId && tempEdgeId) {
          const edgeResult = await createEdgeMutation.mutateAsync({
            workflowId,
            edge: { sourceNodeId: parentNodeId, targetNodeId: childNodeId, edgeType: 'default' },
          });
          const createdEdgeId = edgeResult?.edge?.id;
          if (createdEdgeId) {
            setEdges((eds) =>
              eds.map((e) => (e.id === tempEdgeId ? { ...e, id: createdEdgeId } : e))
            );
          }
        }
      } catch (error) {
        const failureToast = getNodeCrudFailureToast(error, 'Failed to delete node');
        if (failureToast) toast.error(failureToast.message, failureToast.options);
      }
    },
    [
      setNodes,
      setEdges,
      edges,
      selectedNodeForConfig,
      deleteNodeMutation,
      createEdgeMutation,
      workflowId,
      toast,
    ]
  );

  // Handle prepending a node before the first node
  const handlePrependNode = React.useCallback(
    async (nodeType: string) => {
      onEdit?.();
      undoHistory.captureSnapshot('Prepend node');
      const currentNodes = getNodes();
      const nodesWithIncoming = new Set(edges.map((e) => e.target));
      const firstNode = currentNodes.find((n) => !nodesWithIncoming.has(n.id));

      if (!firstNode) {
        const create = onCreateNodeRef.current;
        if (create) {
          void create(
            { type: nodeType, data: getDefaultNodeData(nodeType, workflowFlowTypeRef.current) },
            { autoSelect: true }
          );
        }
        return;
      }

      setIsInsertingNode(true);
      const tempNodeId = `temp-prepend-${Date.now()}`;
      const tempEdgeId = `temp-edge-${Date.now()}`;

      const defaultData = getDefaultNodeData(nodeType, workflowFlowTypeRef.current);
      const optimisticNode: WorkflowNode = {
        id: tempNodeId,
        type: nodeType,
        data: { ...defaultData, isPersisted: false },
      };

      setNodes((nds) => [optimisticNode, ...nds]);
      setEdges((eds) => [...eds, { id: tempEdgeId, source: tempNodeId, target: firstNode.id }]);

      try {
        const wfId = await ensureWorkflow();
        const nodeResult = await createNodeMutation.mutateAsync({
          workflowId: wfId,
          node: {
            nodeType,
            configJson: defaultData,
            orderIndex: 0,
          },
        });
        const newNodeId = nodeResult.node.id;

        const edgeResult = await createEdgeMutation.mutateAsync({
          workflowId: wfId,
          edge: { sourceNodeId: newNodeId, targetNodeId: firstNode.id, edgeType: 'default' },
        });

        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== tempNodeId) return n;
            const updatedData: Record<string, any> = {
              ...n.data,
              isPersisted: true,
              onConfigClick: () => handleNodeConfig({ ...n, id: newNodeId }),
              ...buildNodeQuickCreateHandlers({
                nodeType: n.type as WorkflowNodeType,
                nodeId: newNodeId,
                handleQuickCreate: handleQuickCreateNode,
                handleBranchCreate: handleBranchQuickCreate,
                enableApiSequence: hasApiCatalog,
                enableSandboxAction: hasSandboxActions,
                enableMcp: hasMcp,
              }),
            };
            if (n.type === 'condition') {
              updatedData.enableApiSequence = hasApiCatalog;
            }
            return { ...n, id: newNodeId, data: updatedData };
          })
        );

        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            id: e.id === tempEdgeId ? (edgeResult?.edge?.id ?? e.id) : e.id,
            source: e.source === tempNodeId ? newNodeId : e.source,
            target: e.target === tempNodeId ? newNodeId : e.target,
          }))
        );

        const persistedNode: WorkflowNode = {
          id: newNodeId,
          type: nodeType,
          data: { ...defaultData, isPersisted: true },
        };
        handleNodeConfig(persistedNode);
      } catch (error) {
        setNodes((nds) => nds.filter((n) => n.id !== tempNodeId));
        setEdges((eds) => eds.filter((e) => e.id !== tempEdgeId));
        const failureToast = getNodeCrudFailureToast(error, 'Failed to create node');
        if (failureToast) toast.error(failureToast.message, failureToast.options);
      } finally {
        setIsInsertingNode(false);
      }
    },
    [
      getNodes,
      edges,
      ensureWorkflow,
      createNodeMutation,
      createEdgeMutation,
      handleNodeConfig,
      toast,
    ]
  );

  // Handle edge click
  const handleEdgeClick = React.useCallback(
    (_edgeId: string, _edge: WorkflowEdge, _event: React.MouseEvent) => {
      // Note: edge menu position is managed in the parent component via the returned edgeMenuPosition state
    },
    []
  );

  // Delete the selected edge
  const handleDeleteSelectedEdge = React.useCallback(async () => {
    if (!selectedEdgeId || !workflowId) return;
    onEdit?.();
    undoHistory.captureSnapshot('Delete connection');
    setEdges((eds) => eds.filter((e) => e.id !== selectedEdgeId));
    clearEdgeSelection();

    try {
      await deleteEdgeMutation.mutateAsync(selectedEdgeId);
    } catch {
      toast.error('Failed to delete connection');
    }
  }, [selectedEdgeId, workflowId, deleteEdgeMutation, clearEdgeSelection, toast]);

  // Handle drag-to-connect
  const handleConnect = React.useCallback(
    async (sourceNodeId: string, targetNodeId: string, edgeType?: string) => {
      if (!workflowId) return;
      onEdit?.();
      undoHistory.captureSnapshot('Connect nodes');

      const exists = edges.some((e) => e.source === sourceNodeId && e.target === targetNodeId);
      if (exists) {
        toast.info('Connection already exists');
        return;
      }

      const resolvedEdgeType = edgeType || 'default';
      const tempEdgeId = `temp-edge-${Date.now()}`;
      setEdges((eds) => [
        ...eds,
        {
          id: tempEdgeId,
          source: sourceNodeId,
          target: targetNodeId,
          type: resolvedEdgeType,
          edgeType: resolvedEdgeType,
        },
      ]);

      try {
        const result = await createEdgeMutation.mutateAsync({
          workflowId,
          edge: { sourceNodeId, targetNodeId, edgeType: resolvedEdgeType },
        });
        const realId = result?.edge?.id;
        if (realId) {
          setEdges((eds) => eds.map((e) => (e.id === tempEdgeId ? { ...e, id: realId } : e)));
        }
      } catch {
        toast.error('Failed to create connection');
        setEdges((eds) => eds.filter((e) => e.id !== tempEdgeId));
      }
    },
    [workflowId, edges, createEdgeMutation, toast]
  );

  // Insert a new node on a specific edge — the core primitive. Split out from
  // `handleInsertNodeOnEdge` so callers that already know the target edge id
  // (the eval thread's "Add a step" control, which computes the edge from
  // ordered node position) can go straight through the same CRUD path
  // instead of first driving canvas edge-selection state just to trigger it.
  const insertNodeOnEdgeId = React.useCallback(
    async (edgeId: string, nodeType: string) => {
      if (!workflowId) return;
      onEdit?.();
      undoHistory.captureSnapshot('Insert node');

      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;

      const sourceNodeId = edge.source;
      const targetNodeId = edge.target;
      const originalEdgeType =
        (edge as { type?: string }).type ?? (edge as { edgeType?: string }).edgeType ?? 'default';
      const currentNodes = getNodes();

      setIsInsertingNode(true);

      const defaultData = getDefaultNodeData(nodeType, workflowFlowTypeRef.current);

      try {
        const result = await insertNodeOnEdgeMutation.mutateAsync({
          edgeId,
          node: {
            nodeType,
            configJson: defaultData,
            orderIndex: currentNodes.length,
          },
        });
        const newNodeId = result.node.id;

        const newNodeData: Record<string, any> = {
          ...defaultData,
          isPersisted: true,
          onConfigClick: () =>
            handleNodeConfig({ id: newNodeId, type: nodeType, data: defaultData }),
          ...buildNodeQuickCreateHandlers({
            nodeType: nodeType as WorkflowNodeType,
            nodeId: newNodeId,
            handleQuickCreate: handleQuickCreateNode,
            handleBranchCreate: handleBranchQuickCreate,
            enableApiSequence: hasApiCatalog,
            enableSandboxAction: hasSandboxActions,
            enableMcp: hasMcp,
          }),
        };
        if (nodeType === 'condition') {
          newNodeData.enableApiSequence = hasApiCatalog;
        }
        const newNode: WorkflowNode = {
          id: newNodeId,
          type: nodeType,
          data: newNodeData,
        };

        setNodes((nds) => nds.concat(newNode));
        setEdges((eds) => {
          const filtered = eds.filter((e) => e.id !== edgeId);
          return filtered.concat([
            {
              id: result.sourceEdge.id,
              source: sourceNodeId,
              target: newNodeId,
              type: originalEdgeType,
              edgeType: originalEdgeType,
            },
            {
              id: result.targetEdge.id,
              source: newNodeId,
              target: targetNodeId,
              type: 'default',
              edgeType: 'default',
            },
          ]);
        });

        handleNodeConfig(newNode);
      } catch {
        toast.error('Failed to insert node', { reportError: false });
        workflowDetailRefetch();
      } finally {
        setIsInsertingNode(false);
      }
    },
    [
      workflowId,
      edges,
      getNodes,
      insertNodeOnEdgeMutation,
      handleNodeConfig,
      handleQuickCreateNode,
      handleBranchQuickCreate,
      workflowDetailRefetch,
      toast,
    ]
  );

  // Insert a new node on the currently edge-menu-selected edge (canvas flow).
  //
  // The guards are repeated here rather than left to `insertNodeOnEdgeId`
  // because the selection must survive them: before the split, a missing
  // `workflowId` or a `selectedEdgeId` pointing at an edge that no longer
  // exists returned early with the edge-menu selection still open. Clearing
  // unconditionally would silently close the menu on those paths with nothing
  // to show for it.
  const handleInsertNodeOnEdge = React.useCallback(
    async (nodeType: string) => {
      if (!selectedEdgeId || !workflowId) return;
      const edgeId = selectedEdgeId;
      if (!edges.some((e) => e.id === edgeId)) return;
      clearEdgeSelection();
      await insertNodeOnEdgeId(edgeId, nodeType);
    },
    [selectedEdgeId, workflowId, edges, clearEdgeSelection, insertNodeOnEdgeId]
  );

  // Handle node reorder
  const handleNodeReorder = React.useCallback(
    async (nodeId: string, newIndex: number, chain: string[]) => {
      if (!workflowId) return;
      onEdit?.();
      undoHistory.captureSnapshot('Reorder node');

      const ops = computeReorderEdgeOps(nodeId, newIndex, chain, edges);
      if (ops.edgesToDelete.length === 0 && ops.edgesToCreate.length === 0) return;

      setEdges((eds) => {
        const filtered = eds.filter((e) => !ops.edgesToDelete.includes(e.id));
        const newEdges = ops.edgesToCreate.map((op, i) => ({
          id: `temp-reorder-${Date.now()}-${i}`,
          source: op.source,
          target: op.target,
          type: op.edgeType,
          edgeType: op.edgeType,
        }));
        return [...filtered, ...newEdges];
      });

      try {
        for (const edgeId of ops.edgesToDelete) {
          await deleteEdgeMutation.mutateAsync(edgeId);
        }

        const realEdges: Array<{ tempIndex: number; realId: string }> = [];
        for (let i = 0; i < ops.edgesToCreate.length; i++) {
          const op = ops.edgesToCreate[i];
          const result = await createEdgeMutation.mutateAsync({
            workflowId,
            edge: { sourceNodeId: op.source, targetNodeId: op.target, edgeType: op.edgeType },
          });
          const realId = result?.edge?.id;
          if (realId) {
            realEdges.push({ tempIndex: i, realId });
          }
        }

        if (realEdges.length > 0) {
          setEdges((eds) =>
            eds.map((e) => {
              const match = realEdges.find(
                (r) => e.id === `temp-reorder-${Math.floor(Date.now() / 1000)}-${r.tempIndex}`
              );
              if (!match) {
                const opMatch = realEdges.find((r) => {
                  const op = ops.edgesToCreate[r.tempIndex];
                  return (
                    op &&
                    e.source === op.source &&
                    e.target === op.target &&
                    e.id.startsWith('temp-reorder-')
                  );
                });
                if (opMatch) return { ...e, id: opMatch.realId };
              }
              return match ? { ...e, id: match.realId } : e;
            })
          );
        }
      } catch {
        toast.error('Failed to reorder step');
        workflowDetailRefetch();
      }
    },
    [
      workflowId,
      edges,
      deleteEdgeMutation,
      createEdgeMutation,
      setEdges,
      workflowDetailRefetch,
      toast,
    ]
  );

  // Handle selection change
  const handleSelectionChange = React.useCallback(
    async (ids: Set<string>) => {
      setSelectedNodeIds(ids);
      if (ids.size === 1) {
        const nodeId = [...ids][0];
        const node = nodes.find((n) => n.id === nodeId);
        if (node) {
          await autoSaveIfDirty();
          setSelectedNodeForConfig(node);
          selectSidebarTab('config');
        }
      } else if (ids.size > 1) {
        await autoSaveIfDirty();
        setSelectedNodeForConfig(null);
        selectSidebarTab('config');
      }
    },
    [nodes, autoSaveIfDirty, selectSidebarTab]
  );

  // Handle multi-node deletion — delegates to server-side bulk endpoint
  // which computes reconnections atomically from a single graph snapshot.
  const handleMultiNodeDelete = React.useCallback(
    async (nodeIds: string[]) => {
      onEdit?.();
      undoHistory.captureSnapshot('Delete nodes');

      const deleteSet = new Set(nodeIds);
      const shouldRefetchAfterDelete = !(suggestingRef?.current ?? false);

      // Optimistic UI update. Both normal and suggest modes use the same shared
      // graph algorithm as the server, so the canvas reconnects immediately.
      const optimisticGraph = computeOptimisticBulkDeleteGraph({
        nodes: getNodes(),
        edges,
        deleteNodeIds: nodeIds,
      });
      setNodes(optimisticGraph.nodes);
      setEdges(optimisticGraph.edges);

      if (selectedNodeForConfig && deleteSet.has(selectedNodeForConfig.id)) {
        setSelectedNodeForConfig(null);
      }
      setSelectedNodeIds(new Set());

      try {
        await bulkDeleteNodesMutation.mutateAsync({ nodeIds, reconnect: true });
        // Suggest mode must not refetch because staged edits would be clobbered.
        // Normal mode refetches to reconcile server-minted edge ids.
        if (shouldRefetchAfterDelete) workflowDetailRefetch();
      } catch (error) {
        const failureToast = getNodeCrudFailureToast(error, 'Failed to delete nodes');
        if (failureToast) toast.error(failureToast.message, failureToast.options);
      }
    },
    [
      selectedNodeForConfig,
      bulkDeleteNodesMutation,
      workflowDetailRefetch,
      toast,
      undoHistory,
      onEdit,
      edges,
      getNodes,
      suggestingRef,
      setNodes,
      setEdges,
      setSelectedNodeIds,
      setSelectedNodeForConfig,
    ]
  );

  // Handle group reorder
  const handleMultiNodeReorder = React.useCallback(
    async (nodeIds: string[], newStartIndex: number, chain: string[]) => {
      if (!workflowId) return;
      onEdit?.();
      undoHistory.captureSnapshot('Reorder nodes');

      const ops = computeGroupReorderEdgeOps(nodeIds, newStartIndex, chain, edges);
      if (ops.edgesToDelete.length === 0 && ops.edgesToCreate.length === 0) return;

      setEdges((eds) => {
        const filtered = eds.filter((e) => !ops.edgesToDelete.includes(e.id));
        const newEdges = ops.edgesToCreate.map((op, i) => ({
          id: `temp-group-reorder-${Date.now()}-${i}`,
          source: op.source,
          target: op.target,
          type: op.edgeType,
          edgeType: op.edgeType,
        }));
        return [...filtered, ...newEdges];
      });

      try {
        for (const edgeId of ops.edgesToDelete) {
          await deleteEdgeMutation.mutateAsync(edgeId);
        }

        const realEdges: Array<{ tempIndex: number; realId: string }> = [];
        for (let i = 0; i < ops.edgesToCreate.length; i++) {
          const op = ops.edgesToCreate[i];
          const result = await createEdgeMutation.mutateAsync({
            workflowId,
            edge: { sourceNodeId: op.source, targetNodeId: op.target, edgeType: op.edgeType },
          });
          const realId = result?.edge?.id;
          if (realId) {
            realEdges.push({ tempIndex: i, realId });
          }
        }

        if (realEdges.length > 0) {
          setEdges((eds) =>
            eds.map((e) => {
              const opMatch = realEdges.find((r) => {
                const op = ops.edgesToCreate[r.tempIndex];
                return (
                  op &&
                  e.source === op.source &&
                  e.target === op.target &&
                  e.id.startsWith('temp-group-reorder-')
                );
              });
              if (opMatch) return { ...e, id: opMatch.realId };
              return e;
            })
          );
        }
      } catch {
        toast.error('Failed to reorder steps');
        workflowDetailRefetch();
      }
    },
    [
      workflowId,
      edges,
      deleteEdgeMutation,
      createEdgeMutation,
      setEdges,
      workflowDetailRefetch,
      toast,
    ]
  );

  // Handle orphan node insert on edge
  const handleOrphanInsertOnEdge = React.useCallback(
    async (nodeId: string, edgeId: string) => {
      if (!workflowId) return;
      onEdit?.();
      undoHistory.captureSnapshot('Insert orphan on edge');

      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;

      const sourceNodeId = edge.source;
      const targetNodeId = edge.target;
      const originalEdgeType =
        (edge as { type?: string }).type ?? (edge as { edgeType?: string }).edgeType ?? 'default';

      const tempEdge1Id = `temp-orphan-insert-${Date.now()}-0`;
      const tempEdge2Id = `temp-orphan-insert-${Date.now()}-1`;

      setEdges((eds) => {
        const filtered = eds.filter((e) => e.id !== edgeId);
        return [
          ...filtered,
          {
            id: tempEdge1Id,
            source: sourceNodeId,
            target: nodeId,
            type: originalEdgeType,
            edgeType: originalEdgeType,
          },
          {
            id: tempEdge2Id,
            source: nodeId,
            target: targetNodeId,
            type: 'default',
            edgeType: 'default',
          },
        ];
      });

      try {
        await deleteEdgeMutation.mutateAsync(edgeId);

        const result1 = await createEdgeMutation.mutateAsync({
          workflowId,
          edge: { sourceNodeId, targetNodeId: nodeId, edgeType: originalEdgeType },
        });

        const result2 = await createEdgeMutation.mutateAsync({
          workflowId,
          edge: { sourceNodeId: nodeId, targetNodeId, edgeType: 'default' },
        });

        const realId1 = result1?.edge?.id;
        const realId2 = result2?.edge?.id;

        setEdges((eds) =>
          eds.map((e) => {
            if (e.id === tempEdge1Id && realId1) return { ...e, id: realId1 };
            if (e.id === tempEdge2Id && realId2) return { ...e, id: realId2 };
            return e;
          })
        );
      } catch {
        toast.error('Failed to insert step on connection');
        workflowDetailRefetch();
      }
    },
    [
      workflowId,
      edges,
      deleteEdgeMutation,
      createEdgeMutation,
      setEdges,
      workflowDetailRefetch,
      toast,
    ]
  );

  // Config saves for a node whose create is still in flight. The node only has a
  // client temp id until the POST returns, so a PUT to it would 404/500. Saves
  // queue here (latest config wins) and flush to the real id once the create
  // resolves. Entries stay after settling so a late call with the stale temp id
  // still resolves to the real id.
  const pendingCreatesRef = React.useRef(new Map<string, PendingCreate>());

  // Update node data
  const onUpdateNode = React.useCallback(
    async (requestedNodeId: string, data: Record<string, any>) => {
      onEdit?.();
      undoHistory.captureSnapshot('Update node', { debounceMs: 2000 });

      const pending = pendingCreatesRef.current.get(requestedNodeId);
      // The create failed and the node was removed; nothing to save.
      if (pending?.realId === null) return;
      const nodeId = pending?.realId ?? requestedNodeId;

      // Merge incoming partial data with existing node config so partial
      // updates (e.g. only playbookSteps) don't wipe other fields.
      // sanitizeNodeDataForPersistence strips runtime-only keys (functions,
      // isPersisted, execution state, etc.) that must not be sent to the backend.
      const existingNode = getNodes().find((n) => n.id === nodeId || n.id === requestedNodeId);
      const existingConfig = sanitizeNodeDataForPersistence(
        existingNode?.data as Record<string, any>
      );
      const mergedConfigJson = { ...existingConfig, ...data };

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== nodeId) return node;
          const merged = { ...node.data, ...data };
          // Recompute derived counts from source arrays
          if ('inlineAssertions' in data) {
            merged.inlineAssertionCount = Array.isArray(data.inlineAssertions)
              ? data.inlineAssertions.length
              : 0;
          }
          if ('playbookSteps' in data) {
            merged.playbookStepCount = Array.isArray(data.playbookSteps)
              ? data.playbookSteps.length
              : undefined;
          }
          return { ...node, data: merged };
        })
      );

      try {
        if (pending) {
          // getNodes() lags React commits, so while a create or a save is in
          // flight the entry keeps the node's latest config and every save goes
          // through one ordered chain. Once idle, the live node is authoritative.
          if (pending.realId !== undefined && pending.active === 0) {
            pending.config = mergedConfigJson;
          }
          pending.config = { ...pending.config, ...data };
          pending.active += 1;
          const save = pending.chain
            .then(async () => {
              const realId = await pending.settled;
              if (!realId || pending.sent === pending.config) return;
              const config = pending.config;
              await updateNodeMutation.mutateAsync({
                nodeId: realId,
                data: { configJson: config },
              });
              pending.sent = config;
            })
            .finally(() => {
              pending.active -= 1;
            });
          pending.chain = save.catch(() => {});
          await save;
          return;
        }
        await updateNodeMutation.mutateAsync({ nodeId, data: { configJson: mergedConfigJson } });
      } catch (error) {
        const failureToast = getNodeUpdateFailureToast(error);
        if (failureToast) toast.error(failureToast.message, failureToast.options);
      }
    },
    [setNodes, getNodes, updateNodeMutation, toast]
  );

  // Create node
  const onCreateNode: CreateNodeFn = React.useCallback(
    async (
      nodeConfig: { type: string; data: Record<string, any> },
      options: CreateNodeOptions = {}
    ) => {
      onEdit?.();
      undoHistory.captureSnapshot('Create node');
      const currentNodes = getNodes();

      const {
        sourceNodeId: explicitSourceNodeId,
        autoSelect = false,
        edgeType = 'default',
      } = options;
      const lastNode = currentNodes.length > 0 ? currentNodes[currentNodes.length - 1] : null;
      const effectiveSourceNodeId = explicitSourceNodeId ?? lastNode?.id ?? null;

      const tempId = `${nodeConfig.type}-${Date.now()}`;
      const data: Record<string, any> = {
        ...nodeConfig.data,
        isPersisted: false,
        onConfigClick: () =>
          handleNodeConfig({ id: tempId, type: nodeConfig.type, data: nodeConfig.data }),
      };

      Object.assign(
        data,
        buildNodeQuickCreateHandlers({
          nodeType: nodeConfig.type as WorkflowNodeType,
          nodeId: tempId,
          handleQuickCreate: handleQuickCreateNode,
          handleBranchCreate: handleBranchQuickCreate,
          enableApiSequence: hasApiCatalog,
          enableSandboxAction: hasSandboxActions,
          enableMcp: hasMcp,
        })
      );

      if (nodeConfig.type === 'condition') {
        data.enableApiSequence = hasApiCatalog;
      }

      const newNode: WorkflowNode = { id: tempId, type: nodeConfig.type, data };

      if (autoSelect) {
        handleNodeConfig(newNode);
      }

      setNodes((nds) => nds.concat(newNode));

      let autoConnectSource: string | null = null;
      if (effectiveSourceNodeId) {
        setEdges((eds) => {
          const alreadyExists = eds.some(
            (edge) => edge.source === effectiveSourceNodeId && edge.target === tempId
          );
          if (alreadyExists) return eds;
          return eds.concat({
            id: `${effectiveSourceNodeId}-${tempId}`,
            source: effectiveSourceNodeId,
            target: tempId,
            type: edgeType,
            edgeType: edgeType,
          });
        });
        if (!isTempNodeId(effectiveSourceNodeId)) {
          autoConnectSource = effectiveSourceNodeId;
        }
      }

      let settleCreate!: (realId: string | null) => void;
      const pendingCreate: PendingCreate = {
        settled: new Promise<string | null>((resolve) => {
          settleCreate = resolve;
        }),
        realId: undefined,
        config: sanitizeNodeDataForPersistence(nodeConfig.data),
        sent: null,
        active: 0,
        chain: Promise.resolve(),
      };
      pendingCreatesRef.current.set(tempId, pendingCreate);

      try {
        const wfId = await ensureWorkflow();

        const result = await createNodeMutation.mutateAsync({
          workflowId: wfId,
          node: {
            nodeType: nodeConfig.type,
            configJson: nodeConfig.data,
            canonicalPageId: nodeConfig.data.canonicalPageId,
            canonicalActionId: nodeConfig.data.canonicalActionId,
            orderIndex: currentNodes.length,
          },
        });

        const backendNodeId = result.node.id;
        pendingCreate.realId = backendNodeId;
        // Later saves arrive under the real id; keep them on the same chain.
        pendingCreatesRef.current.set(backendNodeId, pendingCreate);
        settleCreate(backendNodeId);
        let persistedSelection: WorkflowNode | null = null;

        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== tempId) return n;
            const updatedNodeData: Record<string, any> = {
              ...n.data,
              isPersisted: true,
              onConfigClick: () => handleNodeConfig({ ...n, id: backendNodeId }),
              ...buildNodeQuickCreateHandlers({
                nodeType: n.type as WorkflowNodeType,
                nodeId: backendNodeId,
                allowTemp: true,
                handleQuickCreate: handleQuickCreateNode,
                handleBranchCreate: handleBranchQuickCreate,
                enableApiSequence: hasApiCatalog,
                enableSandboxAction: hasSandboxActions,
                enableMcp: hasMcp,
              }),
            };
            if (n.type === 'condition') {
              updatedNodeData.enableApiSequence = hasApiCatalog;
            }
            const updatedNode: WorkflowNode = { ...n, id: backendNodeId, data: updatedNodeData };
            if (autoSelect) {
              persistedSelection = updatedNode;
            }
            return updatedNode;
          })
        );

        if (autoSelect) {
          setSelectedNodeForConfig((current) => {
            if (current && current.id === tempId) {
              return persistedSelection ?? { ...current, id: backendNodeId };
            }
            return current;
          });
        }

        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            source: e.source === tempId ? backendNodeId : e.source,
            target: e.target === tempId ? backendNodeId : e.target,
          }))
        );

        if (autoConnectSource) {
          try {
            const edgeResult = await createEdgeMutation.mutateAsync({
              workflowId: wfId,
              edge: {
                sourceNodeId: autoConnectSource,
                targetNodeId: backendNodeId,
                edgeType: edgeType,
              },
            });
            const createdEdgeId = edgeResult?.edge?.id;
            if (createdEdgeId) {
              setEdges((eds) =>
                eds.map((edge) =>
                  edge.source === autoConnectSource && edge.target === backendNodeId
                    ? { ...edge, id: createdEdgeId }
                    : edge
                )
              );
            }
          } catch {
            toast.error('Failed to persist auto-connected edge');
            setEdges((eds) =>
              eds.filter(
                (edge) => !(edge.source === autoConnectSource && edge.target === backendNodeId)
              )
            );
          }
        }

        return backendNodeId;
      } catch (error) {
        if (pendingCreate.realId === undefined) {
          pendingCreate.realId = null;
          settleCreate(null);
        }
        const failureToast = getNodeCrudFailureToast(
          error,
          'Failed to create node. Please try again.'
        );
        if (failureToast) toast.error(failureToast.message, failureToast.options);
        setNodes((nds) => nds.filter((n) => n.id !== tempId));
        if (effectiveSourceNodeId) {
          setEdges((eds) =>
            eds.filter((edge) => !(edge.source === effectiveSourceNodeId && edge.target === tempId))
          );
        }
        return null;
      }
    },
    [
      getNodes,
      setNodes,
      setEdges,
      handleNodeConfig,
      handleQuickCreateNode,
      handleBranchQuickCreate,
      ensureWorkflow,
      createNodeMutation,
      createEdgeMutation,
      toast,
    ]
  );

  // Keep onCreateNodeRef in sync
  React.useEffect(() => {
    onCreateNodeRef.current = onCreateNode;
  }, [onCreateNode]);

  return {
    onCreateNodeRef,
    onCreateNode,
    onUpdateNode,
    handleVerticalCanvasNodeDelete,
    handlePrependNode,
    handleEdgeClick,
    handleDeleteSelectedEdge,
    handleConnect,
    handleInsertNodeOnEdge,
    insertNodeOnEdgeId,
    handleNodeReorder,
    handleSelectionChange,
    handleMultiNodeDelete,
    handleMultiNodeReorder,
    handleOrphanInsertOnEdge,
  };
}
